import {
  Box,
  Flex,
  Badge,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import type { RockCardProps } from '../dashboard.types';
import { formatDateWithoutTimezone } from '../dashboard.util';

/**
 * Rock Card Component
 * Displays rock in card view with owner/collaborator badges, progress, status, and milestones
 */
export function RockCard({ rock, milestones, onClick }: RockCardProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const completedColor = useColorModeValue('green.600', 'green.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const gray500 = useColorModeValue('gray.500', 'gray.400');

  const openMilestones = milestones.filter((m) => !m.complete);
  const completedMilestones = milestones.filter((m) => m.complete);

  // Calculate progress
  const progress = milestones.length > 0
    ? Math.round((completedMilestones.length / milestones.length) * 100)
    : 0;

  return (
    <Box
      bg={cardBg}
      rounded="lg"
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
      p={4}
      cursor="pointer"
      onClick={onClick}
      _hover={{ shadow: 'md' }}
      transition="shadow 0.2s"
      h="100%"
      w="100%"
      minW={0}
      maxW="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Header: Badges and Progress */}
      <Flex justify="space-between" align="flex-start" mb={4}>
        <Flex wrap="wrap" gap={2}>
          {/* Owner Badge */}
          <Badge 
            colorScheme="blue" 
            px={2} 
            py={0.5} 
            rounded="full" 
            fontSize="xs" 
            fontWeight="medium"
            textTransform="none"
          >
            {rock.owner?.name || 'Unknown'}
          </Badge>

          {/* Collaborator Badges */}
          {rock.collaboratorUsers && rock.collaboratorUsers.length > 0 && (
            <>
              {rock.collaboratorUsers.map((collab) => (
                <Badge
                  key={collab.id}
                  colorScheme="green"
                  px={2}
                  py={0.5}
                  rounded="full"
                  fontSize="xs"
                  fontWeight="medium"
                  textTransform="none"
                >
                  {collab.name}
                </Badge>
              ))}
            </>
          )}

          {/* Status Badge */}
          <Badge
            colorScheme={rock.status === 'on-track' ? 'green' : 'red'}
            px={3}
            py={1}
            rounded="full"
            fontSize="sm"
            fontWeight="medium"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Icon
              as={rock.status === 'on-track' ? CheckCircle : AlertCircle}
              w={3}
              h={3}
            />
          </Badge>
        </Flex>

        {/* Progress Percentage */}
        <Heading size="xl" color={headingColor}>
          {progress}%
        </Heading>
      </Flex>

      {/* Rock Name */}
      <Heading size="md" mb={2} color={headingColor} isTruncated>
        {rock.rock}
      </Heading>

      {/* Measurable */}
      <Text fontSize="sm" color={textColor} mb={3} lineHeight="relaxed" noOfLines={3}>
        {rock.measurable}
      </Text>

      {/* Milestones Section */}
      {milestones.length > 0 && (
        <Box pt={3} borderTopWidth="1px" borderColor={borderColor} flex="1" display="flex" flexDirection="column">
          <VStack spacing={2} align="stretch" flex="1">
            {/* Open Milestones */}
            {openMilestones.length > 0 && (
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1}>
                  ○ Open Milestones:
                </Text>
                <VStack spacing={1} align="stretch" pl={3}>
                  {openMilestones.map((milestone) => (
                    <Flex key={milestone.id} justify="space-between" fontSize="xs" color={textColor}>
                      <Text flex="1" isTruncated>
                        {milestone.task}
                      </Text>
                      <Text color={gray500} ml={2} flexShrink={0}>
                        {milestone.dueDate
                          ? formatDateWithoutTimezone(milestone.dueDate, {
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'No date'}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Completed Milestones */}
            {completedMilestones.length > 0 && (
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={completedColor} mb={1}>
                  ✓ Completed Milestones:
                </Text>
                <VStack spacing={1} align="stretch" pl={3}>
                  {completedMilestones.map((milestone) => (
                    <Flex
                      key={milestone.id}
                      justify="space-between"
                      fontSize="xs"
                      color={completedColor}
                    >
                      <Text flex="1" isTruncated textDecoration="line-through">
                        {milestone.task}
                      </Text>
                      <Text color={completedColor} ml={2} flexShrink={0}>
                        {milestone.completedAt
                          ? formatDateWithoutTimezone(milestone.completedAt, {
                              month: 'short',
                              day: 'numeric',
                            })
                          : milestone.dueDate
                          ? formatDateWithoutTimezone(milestone.dueDate, {
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'Done'}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      )}
      {milestones.length === 0 && <Box flex="1" />}
    </Box>
  );
}
